import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface RevealProps {
    children: ReactNode
    delay?: number
    className?: string
}

// Fades content up as it scrolls into view. Without JavaScript or with reduced motion it is simply visible.
const Reveal: FC<RevealProps> = ({ children, delay = 0, className }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const node = ref.current

        if (!node) return

        if (typeof IntersectionObserver === 'undefined') {
            setVisible(true)
            return
        }

        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                setVisible(true)
                observer.disconnect()
            }
        }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })

        observer.observe(node)

        return () => observer.disconnect()
    }, [])

    return (
        <div
            ref={ref}
            data-reveal={visible ? 'visible' : 'hidden'}
            style={delay ? { transitionDelay: `${delay}ms` } : undefined}
            className={cn(
                'transition duration-700 ease-out motion-reduce:transition-none',
                visible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100',
                className
            )}
        >
            {children}
        </div>
    )
}

export default Reveal
